// External UI
import {
  Anchor,
  Badge,
  Button,
  Loader,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core'
import { DateInput } from '@/components/ui/DateInput'
import { ResourceInput } from '@medplum/react'

// External utilities
import { useState } from 'react'
import { IconCircleCheck } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { useForm, Controller, FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery } from '@tanstack/react-query'
import { createReference, formatDateTime } from '@medplum/core'
import { Patient, Reference } from '@medplum/fhirtypes'
import { BOARD_PRIORITY_VALUES } from '@behuman/core-contract'

// Local hooks and types
import { logError } from '@/utils/errorUtils'
import { useOrpc } from '@/hooks/useClient'
import { useFaxTaskSummaries, useCreateFaxTask, type FaxTask } from '@/hooks/useFaxTasks'
import { FAX_TASK_ACTIONS } from '@/model/middleware-types'

const actionCodes = FAX_TASK_ACTIONS.map((a) => a.code) as [string, ...string[]]

const faxActionsSchema = z
  .object({
    actions: z.array(z.enum(actionCodes)).min(1, 'Select at least one action'),
    otherDescription: z.string().trim().max(500),
    boardId: z.string().min(1, 'Select a board'),
    owner: z.object({ reference: z.string() }).optional(),
    patient: z.object({ reference: z.string() }).optional(),
    dueDate: z.string().nullable(),
    priority: z.enum(BOARD_PRIORITY_VALUES),
  })
  .refine((v) => !v.actions.includes('other') || v.otherDescription.length > 0, {
    message: 'Description is required when "Other" is selected',
    path: ['otherDescription'],
  })

type FaxActionsFormValues = z.input<typeof faxActionsSchema>

const EMPTY_VALUES: FaxActionsFormValues = {
  actions: [],
  otherDescription: '',
  boardId: '',
  owner: undefined,
  patient: undefined,
  dueDate: null,
  priority: 'Medium',
}

export function FaxActionsTab({
  documentReferenceId,
  defaultPatient,
  faxTaskId,
}: {
  documentReferenceId: string
  defaultPatient?: Reference<Patient>
  /** The fax-triage Task id, so the created task can deep-link back to the fax. */
  faxTaskId?: string
}) {
  const orpc = useOrpc()
  const { byDocumentReference } = useFaxTaskSummaries([documentReferenceId])
  // Open work first, completed underneath — the list is a to-do, and a finished
  // task should not push an outstanding one below the fold. Array.sort is stable,
  // so the server's ordering is preserved within each group.
  const tasks = [...(byDocumentReference.get(documentReferenceId)?.tasks ?? [])].sort(
    (a, b) => Number(a.done) - Number(b.done),
  )
  const { data: config, isLoading: isConfigLoading } = useQuery(
    orpc.careTaskConfigGet.queryOptions({ input: {} }),
  )
  const createFaxTask = useCreateFaxTask()

  // Minted once per mounted form, and replaced only after a successful create
  // (never on a memo): the server keys its duplicate-collapse on this id, so a
  // double-click on the same submission collapses into one task, but a
  // deliberate second task on the same fax still creates a new one.
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID())

  // Which half of the Actions tab is showing. Null means "not chosen yet", so the
  // panel opens on the list when the fax already has tasks and on the form when it
  // doesn't — without an effect, and without overriding the user once they pick.
  const [view, setView] = useState<'existing' | 'new' | null>(null)

  // The fax may already be triaged onto a patient; prefill the picker with it
  // but still let the user override or clear it for this task.
  const defaultValues: FaxActionsFormValues = {
    ...EMPTY_VALUES,
    patient: defaultPatient?.reference ? { reference: defaultPatient.reference } : undefined,
  }

  const form = useForm<FaxActionsFormValues>({
    resolver: zodResolver(faxActionsSchema),
    defaultValues,
  })

  const actions = form.watch('actions')

  const onSubmit = async (data: FaxActionsFormValues) => {
    try {
      await createFaxTask.mutateAsync({
        documentReferenceId,
        submissionId,
        faxTaskId,
        boardId: data.boardId,
        actions: data.actions,
        otherDescription: data.actions.includes('other') ? data.otherDescription : undefined,
        priority: data.priority,
        dueDate: data.dueDate ?? undefined,
        patientReference: data.patient?.reference,
        ownerReference: data.owner?.reference,
      })
    } catch {
      // useCreateFaxTask's onError already shows a failure notification. Neither
      // the form nor submissionId may change here: a retry of the same intent
      // must reuse the same submissionId so the server's duplicate-collapse still
      // applies if the request actually landed despite the client-side error.
      return
    }

    // Keep the board the user just picked: a second task on the same fax
    // almost always belongs on the same board, and resetting it to empty left
    // the Select showing a board the form no longer held.
    form.reset({ ...defaultValues, boardId: data.boardId })
    setSubmissionId(crypto.randomUUID())
    // Stay on the form so a second task on the same fax can be created straight
    // away — the "Existing tasks" count ticking up is the confirmation. Without
    // this the view would fall back to the list as soon as the first task exists.
    setView('new')
  }

  const onError = (errors: FieldErrors<FaxActionsFormValues>) => {
    logError('fax', 'Fax action task form validation failed', new Error(JSON.stringify(errors)))
  }

  const boardOptions = (config?.boards ?? []).map((b) => ({ value: b.id, label: b.name }))
  const actionOptions = FAX_TASK_ACTIONS.map((a) => ({ value: a.code, label: a.label }))
  const priorityOptions = BOARD_PRIORITY_VALUES.map((p) => ({ value: p, label: p }))

  const activeView = view ?? (tasks.length > 0 ? 'existing' : 'new')

  return (
    <Stack gap="sm">
      <SegmentedControl
        fullWidth
        value={activeView}
        onChange={(value) => setView(value as 'existing' | 'new')}
        data={[
          {
            value: 'existing',
            label: (
              <span className="inline-flex items-center gap-1.5">
                Existing tasks
                {tasks.length > 0 && (
                  <Badge size="xs" variant="light" color="green" circle>
                    {tasks.length}
                  </Badge>
                )}
              </span>
            ),
          },
          { value: 'new', label: 'New task' },
        ]}
      />

      {activeView === 'existing' &&
        (tasks.length > 0 ? (
          <Stack gap="xs">
            {tasks.map((task) => (
              <TaskCard key={task.itemId} task={task} />
            ))}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">
            No tasks for this fax yet.
          </Text>
        ))}

      <form
        onSubmit={form.handleSubmit(onSubmit, onError)}
        className="flex flex-col gap-2"
        hidden={activeView !== 'new'}
      >
        <Controller
          name="actions"
          control={form.control}
          render={({ field, fieldState }) => (
            <MultiSelect
              label="Actions needed"
              error={fieldState.error?.message}
              data={actionOptions}
              {...field}
            />
          )}
        />

        {actions.includes('other') && (
          <Controller
            name="otherDescription"
            control={form.control}
            render={({ field, fieldState }) => (
              <Textarea
                label="Description"
                error={fieldState.error?.message}
                autosize
                minRows={2}
                {...field}
              />
            )}
          />
        )}

        <Controller
          name="boardId"
          control={form.control}
          render={({ field, fieldState }) => (
            <Select
              label="Board"
              error={fieldState.error?.message}
              data={boardOptions}
              disabled={isConfigLoading}
              rightSection={isConfigLoading ? <Loader size="xs" /> : undefined}
              // Bound explicitly instead of spreading `field`, and the empty
              // value is passed as `null`, not `''`: Mantine's Select only
              // clears the text it displays when `value` is null, so an empty
              // string after form.reset() left the previously chosen board
              // still SHOWING while the form held nothing — the user saw a
              // filled field and got a "Select a board" error on submit.
              name={field.name}
              value={field.value || null}
              onChange={(value) => field.onChange(value ?? '')}
              onBlur={field.onBlur}
            />
          )}
        />

        <Controller
          name="patient"
          control={form.control}
          render={({ field }) => (
            <ResourceInput
              // Same uncontrolled-input issue as the Owner picker below: without
              // remounting on submissionId, a successful create clears the RHF
              // `patient` value but the picker keeps showing the last-picked
              // patient, so the form appears to still target them.
              key={submissionId}
              label="Related patient"
              resourceType="Patient"
              name={field.name}
              defaultValue={field.value}
              onChange={(e) => field.onChange(e ? createReference(e) : undefined)}
            />
          )}
        />

        <Controller
          name="owner"
          control={form.control}
          render={({ field }) => (
            <ResourceInput
              // ResourceInput is uncontrolled — it seeds its selection from
              // defaultValue once and never re-syncs. Without this key, a
              // successful create clears the RHF `owner` value but the picker
              // keeps showing the last-picked practitioner, so the form appears
              // to still target them when the next task will actually be
              // auto-assigned. submissionId rotates only on a successful create
              // (never on every keystroke), so keying on it remounts the picker
              // exactly when the field actually needs to clear.
              key={submissionId}
              label="Owner"
              resourceType="Practitioner"
              name={field.name}
              defaultValue={field.value}
              onChange={(e) => field.onChange(e ? createReference(e) : undefined)}
            />
          )}
        />
        <Text size="xs" c="dimmed" mt={-6}>
          Leave blank to auto-assign to the least-loaded coordinator.
        </Text>

        <Controller
          name="dueDate"
          control={form.control}
          render={({ field, fieldState }) => (
            <DateInput
              label="Due date"
              error={fieldState.error?.message}
              value={field.value}
              onChange={(value) => field.onChange(value || null)}
              clearable
            />
          )}
        />

        <Controller
          name="priority"
          control={form.control}
          render={({ field, fieldState }) => (
            <Select
              label="Priority"
              error={fieldState.error?.message}
              data={priorityOptions}
              allowDeselect={false}
              {...field}
            />
          )}
        />

        <Button loading={form.formState.isSubmitting || createFaxTask.isPending} type="submit">
          Create task
        </Button>
      </form>
    </Stack>
  )
}

function TaskCard({ task }: { task: FaxTask }) {
  const actionsLine = task.actions
    .map((code) => FAX_TASK_ACTIONS.find((a) => a.code === code)?.label ?? code)
    .join(', ')

  return (
    <div className="flex flex-col gap-1 rounded border border-gray-200 p-3">
      <div className="flex items-start justify-between gap-2">
        <Text size="sm" fw={500}>
          {task.name}
        </Text>
        {task.overdue ? (
          <Badge color="red" size="xs">
            Overdue
          </Badge>
        ) : task.done ? (
          <IconCircleCheck
            size={18}
            color="var(--mantine-color-green-6)"
            className="shrink-0"
            aria-label="Completed"
          />
        ) : null}
      </div>
      {/* The title is derived from the actions, so for a single action it repeats the
          label verbatim. Only show the list when it carries something the title does
          not — e.g. "Complete form +1 more" vs "Complete form, Provider review". */}
      {actionsLine && actionsLine !== task.name && (
        <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
          {actionsLine}
        </Text>
      )}
      <Text size="xs" c="dimmed">
        {task.status ?? 'Not started'}
      </Text>
      <Text size="xs" c="dimmed">
        Owner: {task.owner?.display ?? 'Unassigned'}
      </Text>
      {task.dueDate && (
        <Text size="xs" c="dimmed">
          Due {task.dueDate}
        </Text>
      )}
      {task.completion && (
        <Stack gap={2} mt={4} p={6} bg="gray.0" style={{ borderRadius: 4 }}>
          <Text size="xs" style={{ wordBreak: 'break-word' }}>
            {task.completion.note}
          </Text>
          <Text size="xs" c="dimmed">
            Completed by: {task.completion.by.display}
          </Text>
          <Text size="xs" c="dimmed">
            Completed on {formatDateTime(task.completion.at)}
          </Text>
        </Stack>
      )}
      <Anchor
        component={Link}
        size="xs"
        to={`/appointments?tab=care-queue&board=${task.boardId}&item=${task.itemId}`}
      >
        View task
      </Anchor>
    </div>
  )
}
